import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router } from '@angular/router';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { MaterialModule } from '../../../shared/material.module';
import { MatIconModule } from '@angular/material/icon';
import { UserService } from '../../../services/user.service';
import { ChangePasswordRequest } from '../../../models/api/changePasswordRequest';

@Component({
  selector: 'app-change-password',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgxSpinnerModule,
    MaterialModule,
    MatIconModule
  ],
  templateUrl: './change-password.component.html',
  styleUrl: './change-password.component.scss'
})
export class ChangePasswordComponent implements OnInit {
  form: FormGroup;
  hideCurrentPassword = true;
  hideNewPassword = true;
  hideConfirmPassword = true;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private spinnerService: NgxSpinnerService,
    private toast: HotToastService,
    private userService: UserService
  ) {
    this.form = this.fb.group({
      currentPassword: ['', Validators.required],
      newPassword: ['', [
        Validators.required,
        Validators.minLength(8),
        this.passwordStrengthValidator
      ]],
      confirmPassword: ['', Validators.required]
    }, {
      validators: this.passwordMatchValidator
    });
  }

  ngOnInit(): void {
    // Initialize component
  }

  passwordStrengthValidator(control: AbstractControl): ValidationErrors | null {
    const password = control.value;

    if (!password) {
      return null;
    }

    const errors: ValidationErrors = {};

    // Check for at least 8 characters
    if (password.length < 8) {
      errors['minLength'] = true;
    }

    // Check for uppercase letter
    if (!/[A-Z]/.test(password)) {
      errors['uppercase'] = true;
    }

    // Check for lowercase letter
    if (!/[a-z]/.test(password)) {
      errors['lowercase'] = true;
    }

    // Check for number
    if (!/[0-9]/.test(password)) {
      errors['number'] = true;
    }

    // Check for special character
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      errors['specialChar'] = true;
    }

    return Object.keys(errors).length > 0 ? errors : null;
  }

  passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
    const newPassword = control.get('newPassword');
    const confirmPassword = control.get('confirmPassword');

    if (!newPassword || !confirmPassword) {
      return null;
    }

    if (confirmPassword.value === '') {
      return null;
    }

    if (newPassword.value !== confirmPassword.value) {
      return { passwordMismatch: true };
    }

    return null;
  }

  get newPasswordControl() {
    return this.form.get('newPassword');
  }

  hasError(errorName: string): boolean {
    return this.newPasswordControl?.hasError(errorName) || false;
  }

  isRequirementMet(errorName: string): boolean {
    return !this.hasError(errorName) && this.newPasswordControl?.value;
  }

  onSubmit(): void {
    if (this.form.valid) {
      this.spinnerService.show();

      const passwordChangeRequest: ChangePasswordRequest = {
        currentPassword: this.form.value.currentPassword,
        newPassword: this.form.value.newPassword,
        confirmNewPassword: this.form.value.confirmPassword
      };

      this.userService.changePassword(passwordChangeRequest).subscribe({
        next: () => {
          this.spinnerService.hide();
          this.toast.success('Password changed successfully', {
            iconTheme: {
              primary: 'var(--mat-sys-secondary)',
              secondary: '#FFF',
            }
          });
          this.router.navigate(['/']);
        },
        error: (error: any) => {
          this.spinnerService.hide();
          const errorMessage = error?.error?.detail || error?.message || 'Failed to change password';
          this.toast.error('Error changing password: ' + errorMessage, {
            iconTheme: {
              primary: 'var(--mat-sys-error)',
              secondary: '#FFF',
            }
          });
          console.error('Error changing password:', error);
        }
      });
    }
  }

  onCancel(): void {
    this.router.navigate(['/']);
  }
}
